<template>
  <q-dialog
    v-model="visible"
    persistent
    no-esc-dismiss
    no-backdrop-dismiss
    maximized
    transition-show="fade"
    transition-hide="fade"
  >
    <div class="onboarding-gate">
      <div class="gate-overlay"></div>

      <div class="gate-container">
        <q-card class="setup-wizard-card" flat>
          <q-card-section class="wizard-header">
            <div class="brand-section">
              <img
                src="../assets/greenlists-v1.svg"
                alt="GreenLists Logo"
                width="130"
                height="32"
              />
            </div>

            <q-btn
              flat
              dense
              no-caps
              class="logout-btn"
              @click="requestLogout"
              :disable="isNavigating"
            >
              <q-icon name="logout" size="18px" class="q-mr-xs" />
              <span class="logout-text">Logout</span>
            </q-btn>
          </q-card-section>

          <q-card-section class="wizard-content">
            <div class="setup-flow">
              <div class="step-header">
                <h1 class="setup-title">
                  Launch Your Cannabis Business
                  <span class="title-highlight">Operations Platform</span>
                </h1>
                <p class="setup-subtitle">
                  GreenLists saves time and money by automating compliance and
                  fulfillment, while providing real-time "Live Market Data" to
                  help buyers and sellers make data-driven pricing and purchase
                  decisions.
                </p>
                <p class="setup-instruction">
                  Choose your business type to get started:
                </p>
              </div>

              <div class="business-type-section">
                <div class="type-selector-grid">
                  <div
                    v-for="businessType in businessTypes"
                    :key="businessType.value"
                    class="type-card"
                    :class="{ selected: roleChoice === businessType.value }"
                    @click="selectBusinessType(businessType.value)"
                  >
                    <div class="type-card-header">
                      <div class="type-icon">
                        <q-icon
                          :name="businessType.icon"
                          size="24px"
                          :color="
                            roleChoice === businessType.value
                              ? '#1a4a0a'
                              : '#2a6b0e'
                          "
                        />
                      </div>
                      <div class="type-title">{{ businessType.label }}</div>
                    </div>

                    <div class="type-features">
                      <div
                        v-for="feature in businessType.features"
                        :key="feature"
                        class="feature-item"
                      >
                        <q-icon name="check" size="14px" />
                        <span>{{ feature }}</span>
                      </div>
                    </div>

                    <div
                      class="selection-check"
                      v-if="roleChoice === businessType.value"
                    >
                      <q-icon name="check_circle" size="20px" color="#2a6b0e" />
                    </div>
                  </div>
                </div>
              </div>

              <div class="wizard-actions">
                <q-btn
                  @click="startSetup"
                  color="primary"
                  size="lg"
                  unelevated
                  no-caps
                  class="setup-cta"
                  :class="{ pulse: roleChoice }"
                  :disable="!roleChoice"
                  :loading="isNavigating"
                >
                  <template v-if="roleChoice">
                    Set Up Your Business Platform
                  </template>
                  <template v-else> Choose Your Business Type Above </template>
                  <q-icon
                    v-if="roleChoice"
                    name="arrow_forward"
                    size="16px"
                    class="q-ml-sm cta-arrow"
                  />
                </q-btn>

                <div class="setup-promise">
                  <div class="promise-items">
                    <div class="promise-item">
                      <q-icon name="verified_user" size="14px" />
                      <span>METRC integrated</span>
                    </div>
                    <div class="promise-item">
                      <q-icon name="trending_up" size="14px" />
                      <span>Live market data</span>
                    </div>
                    <div class="promise-item">
                      <q-icon name="api" size="14px" />
                      <span>Direct API connections</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>
  </q-dialog>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "OnboardingGate",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "start-setup", "request-logout"],
  data() {
    return {
      isNavigating: false,
      roleChoice: null,
      businessTypes: [
        {
          label: "Retailer (Buy Only)",
          value: "retailer",
          icon: "storefront",
          features: [
            "Easy Multi-Store Purchasing",
            "Data-Driven Purchasing Tools",
            "Fast Re-Orders",
          ],
        },
        {
          label: "Vendor (Sell Only)",
          value: "vendor",
          icon: "inventory_2",
          features: [
            "Simple Invoicing & Payment Tracking",
            "Automated Manifesting",
            "Integrated Transport Scheduling",
          ],
        },
        {
          label: "Vertical (Buy & Sell)",
          value: "both",
          icon: "swap_horiz",
          features: [
            "Regular Listings + Live Auctions",
            "Intelligent Inventory Management",
            "Simple Rec/Med Transfers",
          ],
        },
      ],
    };
  },
  computed: {
    visible: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit("update:modelValue", val);
      },
    },
  },
  methods: {
    ...mapActions("role", ["selectRole", "syncPricingWizard"]),
    selectBusinessType(type) {
      this.roleChoice = type;
    },
    async startSetup() {
      if (!this.roleChoice) {
        this.$q?.notify?.({
          color: "warning",
          message: "Please select your business type to continue.",
        });
        return;
      }
      this.isNavigating = true;
      try {
        await this.selectRole(this.roleChoice);
        await this.syncPricingWizard();

        const pwType = this.roleChoice === "both" ? "mixed" : this.roleChoice;
        this.$store.commit("pricingWizard/SET_USER_TYPE", pwType);

        this.$emit("start-setup");
        this.visible = false;
      } catch (error) {
        console.error("Navigation error:", error);
      } finally {
        this.isNavigating = false;
      }
    },
    requestLogout() {
      this.$emit("request-logout");
    },
  },
};
</script>

<style lang="scss" scoped>
.onboarding-gate {
  position: relative;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 20px;
}

.gate-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 0;
}

.gate-container {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 900px;
}

.setup-wizard-card {
  background: rgba(255, 255, 255, 0.98);
  border-radius: 24px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(20px);
  overflow: hidden;
}

.wizard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .brand-section {
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
}

.logout-btn {
  color: #6b7280;
  font-size: 14px;
  padding: 6px 12px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(239, 68, 68, 0.08);
    color: #dc2626;

    .q-icon {
      transform: translateX(-2px);
    }
  }

  .q-icon {
    transition: transform 0.2s ease;
  }

  .logout-text {
    @media (max-width: 480px) {
      display: none;
    }
  }
}

.wizard-content {
  padding-top: 0;
}

.setup-flow {
  max-width: 800px;
  margin: 0 auto;
}

.step-header {
  text-align: center;
  margin-bottom: 26px;
}

.setup-title {
  font-size: 2.25rem;
  font-weight: 800;
  color: #111827;
  margin: 0 0 16px 0;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
}

.title-highlight {
  background: linear-gradient(135deg, #2a6b0e 0%, #21ba45 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.setup-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 0 0 8px 0;
  line-height: 1.5;
}

.setup-instruction {
  font-size: 1.1rem;
  margin: 18px 0 0 0;
  font-weight: 500;
}

.business-type-section {
  margin-bottom: 40px;
}

.type-selector-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

.type-card {
  position: relative;
  background: white;
  border: 2px solid rgba(226, 232, 240, 0.8);
  border-radius: 16px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);

  &:hover:not(.selected) {
    border-color: rgba(42, 107, 14, 0.4);
    box-shadow: 0 8px 25px rgba(42, 107, 14, 0.15);
  }

  &.selected {
    background: rgba(42, 107, 14, 0.04);
    border-color: #2a6b0e;
    box-shadow: 0 4px 12px rgba(42, 107, 14, 0.12);

    .type-title {
      color: #1a4a0a;
    }

    .feature-item {
      color: #374151;

      .q-icon {
        color: #2a6b0e;
      }
    }
  }
}

.type-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.type-icon {
  width: 36px;
  height: 36px;
  background: rgba(42, 107, 14, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  .selected & {
    background: rgba(255, 255, 255, 0.2);
  }
}

.type-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  transition: color 0.2s ease;
}

.type-features {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
  transition: color 0.2s ease;

  .q-icon {
    color: #21ba45;
    flex-shrink: 0;
  }
}

.selection-check {
  position: absolute;
  top: 12px;
  right: 12px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(42, 107, 14, 0.2);
}

.wizard-actions {
  text-align: center;
}

.setup-cta {
  font-size: 16px;
  font-weight: 600;
  padding: 18px 36px;
  border-radius: 12px;
  margin-bottom: 24px;
  min-width: 280px;
  background: linear-gradient(135deg, #2a6b0e 0%, #21ba45 100%);
  box-shadow: 0 4px 14px rgba(42, 107, 14, 0.3);
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(42, 107, 14, 0.4);

    .cta-arrow {
      transform: translateX(4px);
    }
  }

  &:disabled {
    background: #9ca3af;
    box-shadow: 0 2px 4px rgba(156, 163, 175, 0.2);
    transform: none;
  }

  &.pulse {
    animation: pulse 2s infinite;
  }
}

.cta-arrow {
  transition: transform 0.2s ease;
}

.setup-promise {
  .promise-items {
    display: flex;
    justify-content: center;
    gap: 32px;
    flex-wrap: wrap;

    @media (max-width: 768px) {
      gap: 20px;
    }
  }

  .promise-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: #6b7280;
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 4px 14px rgba(42, 107, 14, 0.3);
  }
  50% {
    box-shadow: 0 4px 14px rgba(42, 107, 14, 0.5),
      0 0 0 4px rgba(42, 107, 14, 0.1);
  }
}

.setup-wizard-card {
  animation: slideInUp 0.6s ease-out;
}
</style>
